import { useCallback, useState } from "react";

/**
 * Small things the browser remembers about how somebody works.
 *
 * Preferences, not data. Nothing here is ever read back by the server, nothing
 * here decides what anybody may see, and losing the lot costs a click — which
 * is the whole reason it can live in `localStorage` at all. Anything with a
 * consequence belongs on the account, where it survives a different laptop and
 * cannot be edited from the console.
 *
 * Two things make this more than a one-line wrapper.
 *
 * **`localStorage` throws.** Not only on write, and not only when it is full:
 * *reaching* `window.localStorage` raises a `SecurityError` outright where
 * cookies are blocked for the site, which some people have on by default and
 * some workplaces set centrally. A diary that will not render because somebody
 * tightened their browser settings is a worse bug than the one being fixed
 * here, so every access is guarded and a failure means "no preference" rather
 * than an error.
 *
 * **What comes back is not what was written.** It is a string somebody can
 * edit, left over from a version of the portal that may have offered choices
 * this one does not. So a stored value is checked against what is on offer now
 * and discarded when it is not one of them, rather than trusted because it is
 * there.
 */

/**
 * Namespaced, because this is the school's own domain and the portal is not
 * necessarily the only thing that will ever run on it.
 */
const PREFIX = "wmf.";

/**
 * Not `window.localStorage` directly — see the note above about it throwing on
 * access. Null means "this browser will not remember anything", which every
 * caller here treats as an ordinary answer.
 */
function browserStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * A remembered choice from a fixed set, or null.
 *
 * The set is passed in rather than the type being trusted: a value stored by
 * an older version of the portal, or typed into the console by somebody
 * curious, is a string like any other.
 */
export function readChoice<T extends string>(
  key: string,
  allowed: readonly T[],
  storage: Storage | null = browserStorage(),
): T | null {
  try {
    const stored = storage?.getItem(PREFIX + key);
    if (stored === null || stored === undefined) return null;
    return allowed.find((option) => option === stored) ?? null;
  } catch {
    return null;
  }
}

/**
 * Remember a choice, or quietly fail to.
 *
 * A full or refusing store is not worth telling anybody about: the screen
 * already does what they just asked, and the only loss is that it will not
 * still be doing it tomorrow.
 */
export function writeChoice(
  key: string,
  value: string,
  storage: Storage | null = browserStorage(),
): void {
  try {
    storage?.setItem(PREFIX + key, value);
  } catch {
    // Deliberately nothing.
  }
}

/**
 * `useState` for a choice the browser should remember.
 *
 * Read once, when the component first mounts. Watching the store for outside
 * changes would be work in aid of a second tab of the same diary disagreeing
 * about which view it is on, which is not a problem anybody has.
 */
export function useRememberedChoice<T extends string>(
  key: string,
  allowed: readonly T[],
  fallback: T,
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => readChoice(key, allowed) ?? fallback);

  const choose = useCallback(
    (next: T) => {
      setValue(next);
      writeChoice(key, next);
    },
    [key],
  );

  return [value, choose];
}
